import React, { useState, useEffect, useCallback } from 'react';
import { 
  Gavel, 
  MapPin, 
  Calendar, 
  CurrencyEur, 
  House, 
  Buildings,
  Storefront,
  Car,
  TreeEvergreen,
  ArrowSquareOut,
  MagnifyingGlass,
  Funnel,
  ArrowsClockwise,
  Eye,
  Heart,
  Warning,
  CheckCircle,
  Info
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { formatCurrency } from '../utils/formatters';

// Create a simple axios-like fetch for Licitor API
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const licitorApi = {
  getAuctions: async (refresh = false) => {
    const response = await fetch(`${BACKEND_URL}/api/licitor/auctions?refresh=${refresh}`, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    if (!response.ok) throw new Error('Failed to fetch auctions');
    return response.json();
  }
};

// Loading skeleton component
const AuctionCardSkeleton = () => (
  <Card className="animate-pulse">
    <CardContent className="p-4">
      <div className="flex gap-4">
        <div className="w-24 h-24 bg-muted rounded-lg"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-muted rounded w-3/4"></div>
          <div className="h-3 bg-muted rounded w-1/2"></div>
          <div className="h-3 bg-muted rounded w-1/4"></div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Empty state component
const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <Icon size={48} className="text-muted-foreground/50 mb-4" />
    <h3 className="text-lg font-semibold text-muted-foreground">{title}</h3>
    <p className="text-sm text-muted-foreground/70 mt-1 max-w-md">{description}</p>
  </div>
);

// Property type icons mapping
const PROPERTY_TYPE_ICONS = {
  'appartement': Buildings,
  'studio': House,
  'chambre': House,
  'maison': House,
  'pavillon': House,
  'propriété': House,
  'immeuble': Buildings,
  'local': Storefront,
  'commerce': Storefront,
  'bureaux': Buildings,
  'parking': Car,
  'garage': Car,
  'box': Car,
  'terrain': TreeEvergreen,
  'cave': House,
  'default': House
};

// Get icon for property type
const getPropertyTypeIcon = (type) => {
  const lowerType = type?.toLowerCase() || '';
  for (const [key, Icon] of Object.entries(PROPERTY_TYPE_ICONS)) {
    if (lowerType.includes(key)) return Icon;
  }
  return PROPERTY_TYPE_ICONS.default;
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return 'Date inconnue';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  } catch {
    return dateStr;
  }
};

// AI Verdict Badge Component
const AIVerdictBadge = ({ verdict, score, reason }) => {
  const config = {
    HOT: {
      label: "HOT",
      color: "bg-gradient-to-r from-orange-500 to-red-500 text-white",
      icon: "🔥",
      border: "ring-2 ring-red-500/50"
    },
    ATTRACTIVE: {
      label: "Intéressant",
      color: "bg-gradient-to-r from-emerald-500 to-green-600 text-white",
      icon: "✨",
      border: "ring-2 ring-emerald-500/50"
    },
    NEUTRAL: {
      label: "À étudier",
      color: "bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900",
      icon: "🔍",
      border: "ring-2 ring-amber-400/50"
    },
    NO_GO: {
      label: "Déconseillé",
      color: "bg-gradient-to-r from-gray-500 to-gray-600 text-white",
      icon: "⛔",
      border: "ring-2 ring-gray-500/50"
    }
  };

  const cfg = config[verdict] || config.NEUTRAL;

  return (
    <div className="space-y-1">
      <div className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold shadow-lg", cfg.color, cfg.border)}>
        <span>{cfg.icon}</span>
        <span>{cfg.label}</span>
        {score && <span className="ml-1 opacity-80">({score}%)</span>}
      </div>
      {reason && (
        <p className="text-xs text-muted-foreground italic pl-1">{reason}</p>
      )}
    </div>
  );
};

// Auction card component
const AuctionCard = ({ auction, onAnalyze, onFavorite, isFavorite }) => {
  const PropertyIcon = getPropertyTypeIcon(auction.property_type);
  const pricePerSqm = auction.surface_sqm && auction.starting_price 
    ? Math.round(auction.starting_price / auction.surface_sqm) 
    : null;

  // Determine card border based on AI verdict
  const getBorderColor = () => {
    switch (auction.ai_verdict) {
      case 'HOT': return 'border-l-red-500 border-l-4';
      case 'ATTRACTIVE': return 'border-l-emerald-500 border-l-4';
      case 'NEUTRAL': return 'border-l-amber-400 border-l-4';
      case 'NO_GO': return 'border-l-gray-400 border-l-4';
      default: return auction.days_until_auction <= 7 ? "border-l-red-500 border-l-4" :
        auction.days_until_auction <= 14 ? "border-l-amber-500 border-l-4" : "border-l-emerald-500 border-l-4";
    }
  };

  return (
    <Card 
      className={cn(
        "group hover:shadow-lg transition-all duration-200 cursor-pointer relative overflow-hidden",
        getBorderColor()
      )}
      data-testid={`auction-card-${auction.id}`}
    >
      <CardContent className="p-4">
        {/* AI Verdict Badge - Prominent Position */}
        {auction.ai_verdict && (
          <div className="mb-3 pb-3 border-b">
            <AIVerdictBadge 
              verdict={auction.ai_verdict} 
              score={auction.ai_score}
              reason={auction.ai_verdict_reason}
            />
          </div>
        )}

        <div className="flex gap-4">
          {/* Property Type Icon */}
          <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-[#F8FAF9] flex items-center justify-center">
            <PropertyIcon size={32} className="text-[#3A4A40]" />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Header row */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-semibold text-foreground truncate">
                  {auction.property_type}
                </h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                  <MapPin size={14} />
                  {auction.location}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn("h-8 w-8 p-0", isFavorite && "text-red-500")}
                  onClick={(e) => { e.stopPropagation(); onFavorite?.(auction); }}
                  title="Ajouter aux favoris"
                >
                  <Heart size={18} weight={isFavorite ? "fill" : "regular"} />
                </Button>
                <a
                  href={auction.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="h-8 w-8 p-0 flex items-center justify-center text-muted-foreground hover:text-foreground"
                  title="Voir sur Licitor"
                >
                  <ArrowSquareOut size={18} />
                </a>
              </div>
            </div>

            {/* Details row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
              {auction.surface_sqm && (
                <span className="text-muted-foreground">{auction.surface_sqm} m²</span>
              )}
              {auction.rooms && (
                <span className="text-muted-foreground">{auction.rooms}</span>
              )}
              {auction.floor && (
                <span className="text-muted-foreground">{auction.floor}</span>
              )}
              {auction.occupancy_status && (
                <Badge variant={auction.occupancy_status === 'Inoccupé' ? 'secondary' : 'outline'} className="text-xs">
                  {auction.occupancy_status}
                </Badge>
              )}
            </div>

            {/* Price and Date row */}
            <div className="flex items-end justify-between mt-3 pt-3 border-t">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Mise à prix</p>
                <p className="text-lg font-bold font-tabular text-[#3A4A40]">
                  {formatCurrency(auction.starting_price)}
                </p>
                {pricePerSqm && (
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(pricePerSqm)}/m²
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Vente</p>
                <p className="text-sm font-medium flex items-center gap-1">
                  <Calendar size={14} />
                  {formatDate(auction.auction_date)}
                </p>
                {auction.days_until_auction !== undefined && auction.days_until_auction !== null && (
                  <Badge 
                    variant={auction.days_until_auction <= 7 ? 'destructive' : 
                             auction.days_until_auction <= 14 ? 'warning' : 'secondary'}
                    className="text-xs mt-1"
                  >
                    {auction.days_until_auction === 0 ? "Aujourd'hui" :
                     auction.days_until_auction === 1 ? "Demain" :
                     `Dans ${auction.days_until_auction} jours`}
                  </Badge>
                )}
              </div>
            </div>

            {/* Action button */}
            <div className="mt-3">
              <Button 
                size="sm" 
                className="w-full bg-[#3A4A40] hover:bg-[#2E3B33]"
                onClick={(e) => { e.stopPropagation(); onAnalyze?.(auction); }}
                data-testid={`analyze-auction-${auction.id}`}
              >
                <MagnifyingGlass size={16} className="mr-1" />
                Analyser ce bien
              </Button>
            </div>
          </div>
        </div>

        {/* Additional info */}
        {auction.description && (
          <p className="text-xs text-muted-foreground mt-3 line-clamp-2">
            {auction.description}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Stats card component
const StatsCard = ({ icon: Icon, label, value, subValue, color = "text-foreground" }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={cn("p-2 rounded-lg bg-muted", color.includes('emerald') && "bg-emerald-100", color.includes('amber') && "bg-amber-100", color.includes('red') && "bg-red-100")}>
          <Icon size={20} className={color} />
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className={cn("text-xl font-bold font-tabular", color)}>{value}</p>
          {subValue && <p className="text-xs text-muted-foreground">{subValue}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Main Licitor Page Component
const Licitor = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('all');
  const [priceRange, setPriceRange] = useState('all');

  // Fetch auctions from backend
  const fetchAuctions = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const data = await licitorApi.getAuctions(forceRefresh);
      
      setAuctions(data.auctions || []);
      setLastRefresh(data.last_refresh);
      
      if (forceRefresh) {
        toast.success('Données mises à jour depuis Licitor');
      }
    } catch (err) {
      console.error('Failed to fetch auctions:', err);
      setError('Impossible de charger les enchères. Veuillez réessayer.');
      toast.error('Erreur de chargement des enchères');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAuctions();
    // Load favorites from localStorage
    const savedFavorites = localStorage.getItem('licitor_favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, [fetchAuctions]);

  // Handle favorite toggle
  const handleFavorite = (auction) => {
    setFavorites(prev => {
      const isFav = prev.some(f => f.id === auction.id);
      const newFavorites = isFav 
        ? prev.filter(f => f.id !== auction.id)
        : [...prev, auction];
      localStorage.setItem('licitor_favorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  // Handle analyze - navigate to quick analyzer with prefilled data
  const handleAnalyze = (auction) => {
    // Store auction data in sessionStorage for the analyzer
    sessionStorage.setItem('licitor_auction', JSON.stringify(auction));
    window.location.href = `/deal-analyzer?source=licitor&auction_id=${auction.id}`;
  };

  // Filter auctions
  const filteredAuctions = auctions.filter(auction => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        auction.property_type?.toLowerCase().includes(query) ||
        auction.location?.toLowerCase().includes(query) ||
        auction.description?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Location filter
    if (locationFilter !== 'all' && !auction.location?.toLowerCase().includes(locationFilter.toLowerCase())) {
      return false;
    }

    // Property type filter
    if (propertyTypeFilter !== 'all' && !auction.property_type?.toLowerCase().includes(propertyTypeFilter.toLowerCase())) {
      return false;
    }

    // Price range filter
    if (priceRange !== 'all') {
      const price = auction.starting_price || 0;
      switch (priceRange) {
        case 'under100k': if (price >= 100000) return false; break;
        case '100k-300k': if (price < 100000 || price >= 300000) return false; break;
        case '300k-500k': if (price < 300000 || price >= 500000) return false; break;
        case 'over500k': if (price < 500000) return false; break;
        default: break;
      }
    }

    return true;
  });

  // Calculate stats
  const stats = {
    total: auctions.length,
    thisWeek: auctions.filter(a => a.days_until_auction <= 7).length,
    avgPrice: auctions.length > 0 
      ? Math.round(auctions.reduce((sum, a) => sum + (a.starting_price || 0), 0) / auctions.length)
      : 0,
    hot: auctions.filter(a => a.ai_verdict === 'HOT').length,
    attractive: auctions.filter(a => a.ai_verdict === 'ATTRACTIVE').length,
    neutral: auctions.filter(a => a.ai_verdict === 'NEUTRAL').length,
    nogo: auctions.filter(a => a.ai_verdict === 'NO_GO').length
  };

  // Unique locations and property types for filters
  const locations = [...new Set(auctions.map(a => {
    const match = a.location?.match(/Paris \d+|Hauts de Seine|Seine Saint Denis|Val de Marne|Essonne|Yvelines|Val d'Oise|Seine et Marne/i);
    return match ? match[0] : null;
  }).filter(Boolean))];

  const propertyTypes = [...new Set(auctions.map(a => {
    const type = a.property_type?.split(' ')[1] || a.property_type;
    return type;
  }).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="licitor-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Gavel size={28} />
            Enchères Immobilières
          </h1>
          <p className="text-muted-foreground mt-1">
            Ventes aux enchères à Paris et Île-de-France via Licitor
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastRefresh && (
            <span className="text-xs text-muted-foreground">
              Mis à jour: {new Date(lastRefresh).toLocaleString('fr-FR')}
            </span>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchAuctions(true)}
            disabled={refreshing}
            data-testid="refresh-auctions-btn"
          >
            <ArrowsClockwise size={16} className={cn("mr-1", refreshing && "animate-spin")} />
            {refreshing ? 'Actualisation...' : 'Actualiser'}
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard
          icon={Gavel}
          label="Total Enchères"
          value={stats.total}
          subValue="Paris & IDF"
        />
        <StatsCard
          icon={CheckCircle}
          label="Opportunités HOT"
          value={stats.hot}
          subValue={`${stats.attractive} intéressantes`}
          color="text-red-600"
        />
        <StatsCard
          icon={CurrencyEur}
          label="Prix Moyen"
          value={formatCurrency(stats.avgPrice)}
          subValue="Mise à prix"
        />
        <StatsCard
          icon={Calendar}
          label="Cette Semaine"
          value={stats.thisWeek}
          subValue="Prochains 7 jours"
          color="text-amber-600"
        />
      </div>

      {/* AI Verdict Quick Filters */}
      <div className="flex flex-wrap gap-2">
        <Badge 
          variant="outline" 
          className="cursor-pointer hover:bg-red-50 border-red-300 text-red-700 px-3 py-1.5"
          onClick={() => setSearchQuery('')}
          data-testid="filter-hot"
        >
          🔥 HOT ({stats.hot})
        </Badge>
        <Badge 
          variant="outline" 
          className="cursor-pointer hover:bg-emerald-50 border-emerald-300 text-emerald-700 px-3 py-1.5"
          onClick={() => setSearchQuery('')}
          data-testid="filter-attractive"
        >
          ✨ Intéressant ({stats.attractive})
        </Badge>
        <Badge 
          variant="outline" 
          className="cursor-pointer hover:bg-amber-50 border-amber-300 text-amber-700 px-3 py-1.5"
          onClick={() => setSearchQuery('')}
          data-testid="filter-neutral"
        >
          🔍 À étudier ({stats.neutral})
        </Badge>
        <Badge 
          variant="outline" 
          className="cursor-pointer hover:bg-gray-100 border-gray-300 text-gray-600 px-3 py-1.5"
          onClick={() => setSearchQuery('')}
          data-testid="filter-nogo"
        >
          ⛔ Déconseillé ({stats.nogo})
        </Badge>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par adresse, type de bien..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-auctions"
                />
              </div>
            </div>

            {/* Location Filter */}
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="location-filter">
                <SelectValue placeholder="Localisation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les zones</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Property Type Filter */}
            <Select value={propertyTypeFilter} onValueChange={setPropertyTypeFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="type-filter">
                <SelectValue placeholder="Type de bien" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="appartement">Appartement</SelectItem>
                <SelectItem value="maison">Maison</SelectItem>
                <SelectItem value="studio">Studio</SelectItem>
                <SelectItem value="local">Local commercial</SelectItem>
                <SelectItem value="parking">Parking/Garage</SelectItem>
              </SelectContent>
            </Select>

            {/* Price Range Filter */}
            <Select value={priceRange} onValueChange={setPriceRange}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="price-filter">
                <SelectValue placeholder="Prix" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les prix</SelectItem>
                <SelectItem value="under100k">Moins de 100K €</SelectItem>
                <SelectItem value="100k-300k">100K - 300K €</SelectItem>
                <SelectItem value="300k-500k">300K - 500K €</SelectItem>
                <SelectItem value="over500k">Plus de 500K €</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">
            Toutes ({filteredAuctions.length})
          </TabsTrigger>
          <TabsTrigger value="hot" className="text-red-600">
            🔥 HOT ({filteredAuctions.filter(a => a.ai_verdict === 'HOT').length})
          </TabsTrigger>
          <TabsTrigger value="attractive" className="text-emerald-600">
            Intéressant ({filteredAuctions.filter(a => a.ai_verdict === 'ATTRACTIVE').length})
          </TabsTrigger>
          <TabsTrigger value="urgent">
            Urgentes ({filteredAuctions.filter(a => a.days_until_auction <= 7).length})
          </TabsTrigger>
          <TabsTrigger value="favorites">
            Favoris ({favorites.length})
          </TabsTrigger>
        </TabsList>

        {/* All Auctions */}
        <TabsContent value="all" className="space-y-4">
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => <AuctionCardSkeleton key={i} />)}
            </div>
          ) : error ? (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-6 text-center">
                <Warning size={48} className="mx-auto mb-4 text-red-500" />
                <p className="text-red-700">{error}</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => fetchAuctions(true)}
                >
                  Réessayer
                </Button>
              </CardContent>
            </Card>
          ) : filteredAuctions.length === 0 ? (
            <EmptyState
              icon={Gavel}
              title="Aucune enchère trouvée"
              description="Essayez de modifier vos filtres ou actualisez les données."
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredAuctions.map(auction => (
                <AuctionCard
                  key={auction.id}
                  auction={auction}
                  onAnalyze={handleAnalyze}
                  onFavorite={handleFavorite}
                  isFavorite={favorites.some(f => f.id === auction.id)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Urgent Auctions */}
        <TabsContent value="urgent" className="space-y-4">
          {filteredAuctions.filter(a => a.days_until_auction <= 7).length === 0 ? (
            <EmptyState
              icon={Calendar}
              title="Aucune enchère urgente"
              description="Aucune vente prévue dans les 7 prochains jours."
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredAuctions
                .filter(a => a.days_until_auction <= 7)
                .sort((a, b) => a.days_until_auction - b.days_until_auction)
                .map(auction => (
                  <AuctionCard
                    key={auction.id}
                    auction={auction}
                    onAnalyze={handleAnalyze}
                    onFavorite={handleFavorite}
                    isFavorite={favorites.some(f => f.id === auction.id)}
                  />
                ))}
            </div>
          )}
        </TabsContent>

        {/* HOT Auctions */}
        <TabsContent value="hot" className="space-y-4">
          {filteredAuctions.filter(a => a.ai_verdict === 'HOT').length === 0 ? (
            <EmptyState
              icon={CheckCircle}
              title="Aucune opportunité HOT"
              description="Les meilleures opportunités apparaîtront ici."
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredAuctions
                .filter(a => a.ai_verdict === 'HOT')
                .sort((a, b) => (b.ai_score || 0) - (a.ai_score || 0))
                .map(auction => (
                  <AuctionCard
                    key={auction.id}
                    auction={auction}
                    onAnalyze={handleAnalyze}
                    onFavorite={handleFavorite}
                    isFavorite={favorites.some(f => f.id === auction.id)}
                  />
                ))}
            </div>
          )}
        </TabsContent>

        {/* Attractive Auctions */}
        <TabsContent value="attractive" className="space-y-4">
          {filteredAuctions.filter(a => a.ai_verdict === 'ATTRACTIVE').length === 0 ? (
            <EmptyState
              icon={CheckCircle}
              title="Aucune opportunité intéressante"
              description="Les opportunités intéressantes apparaîtront ici."
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredAuctions
                .filter(a => a.ai_verdict === 'ATTRACTIVE')
                .sort((a, b) => (b.ai_score || 0) - (a.ai_score || 0))
                .map(auction => (
                  <AuctionCard
                    key={auction.id}
                    auction={auction}
                    onAnalyze={handleAnalyze}
                    onFavorite={handleFavorite}
                    isFavorite={favorites.some(f => f.id === auction.id)}
                  />
                ))}
            </div>
          )}
        </TabsContent>

        {/* Favorites */}
        <TabsContent value="favorites" className="space-y-4">
          {favorites.length === 0 ? (
            <EmptyState
              icon={Heart}
              title="Aucun favori"
              description="Cliquez sur le coeur pour ajouter des enchères à vos favoris."
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {favorites.map(auction => (
                <AuctionCard
                  key={auction.id}
                  auction={auction}
                  onAnalyze={handleAnalyze}
                  onFavorite={handleFavorite}
                  isFavorite={true}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900">À propos des enchères immobilières</h4>
              <p className="text-sm text-blue-700 mt-1">
                Les ventes aux enchères immobilières offrent souvent des opportunités d'acquisition à des prix inférieurs au marché. 
                Avant d'enchérir, consultez le cahier des charges et effectuez une visite sur place. 
                Une consignation (généralement 10% de la mise à prix) est requise pour participer.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Licitor;
